// v10/src/components/FlowResults.js
// Pantalla de resumen al finalizar el Flow Mode
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, AlertTriangle, Clock, Zap, ArrowRight, FileDown, Loader } from 'lucide-react';
import jsPDF from 'jspdf';
import { useTranslation } from 'react-i18next';

const formatTime = (ms) => {
  if (!ms) return '0s';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const secs    = seconds % 60;
  return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`;
};

const sanitizePDF = (text) => {
  if (!text) return '';
  return String(text)
    .normalize('NFC')
    .replace(/[^\x20-\x7E\u00C0-\u00FF]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/_/g, '-')
    .trim();
};

const StatCard = ({ icon: Icon, label, value, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, type: 'spring', stiffness: 120, damping: 18 }}
    style={{
      flex: 1, minWidth: '120px', padding: '16px', borderRadius: '18px',
      background: `${color}10`, border: `1.5px solid ${color}25`,
      textAlign: 'center',
    }}
  >
    <Icon style={{ width: '22px', height: '22px', color, margin: '0 auto 8px', display: 'block' }} />
    <p style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#f1f5f9' }}>{value}</p>
    <p style={{ margin: '4px 0 0', fontSize: '11px', fontWeight: 600, color: '#64748b' }}>{label}</p>
  </motion.div>
);


// ── Signature Pad ─────────────────────────────────────────────────
const SignaturePad = ({ onSave, onClear }) => {
  const { t } = useTranslation();
  const canvasRef = React.useRef(null);
  const drawing   = React.useRef(false);
  const [hasSignature, setHasSignature] = React.useState(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches) return {
      x: (e.touches[0].clientX - rect.left) * (canvas.width / rect.width),
      y: (e.touches[0].clientY - rect.top)  * (canvas.height / rect.height),
    };
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top)  * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => { e.preventDefault(); const c = canvasRef.current; const ctx = c.getContext('2d'); const p = getPos(e, c); drawing.current = true; ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw      = (e) => { e.preventDefault(); if (!drawing.current) return; const c = canvasRef.current; const ctx = c.getContext('2d'); const p = getPos(e, c); ctx.lineTo(p.x, p.y); ctx.strokeStyle = '#93c5fd'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke(); setHasSignature(true); };
  const stopDraw  = (e) => { e.preventDefault(); drawing.current = false; };

  const handleClear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasSignature(false); onClear(); };
  const handleSave  = () => { if (!hasSignature) return; onSave(canvasRef.current.toDataURL('image/png')); };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
      <div style={{ position:'relative', borderRadius:'14px', overflow:'hidden', border:'1px solid rgba(99,179,237,0.3)', background:'rgba(15,25,50,0.6)' }}>
        <canvas ref={canvasRef} width={400} height={120}
          style={{ width:'100%', height:'100px', display:'block', cursor:'crosshair', touchAction:'none' }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
        />
        {!hasSignature && (
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
            <span style={{ fontSize:'12px', color:'rgba(255,255,255,0.2)', fontWeight:600 }}>✍️ Firmá acá con el dedo</span>
          </div>
        )}
      </div>
      <div style={{ display:'flex', gap:'8px' }}>
        <button onClick={handleClear}
          style={{ flex:1, padding:'8px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'rgba(255,255,255,0.5)', fontSize:'12px', fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
          Borrar
        </button>
        <motion.button onClick={handleSave} disabled={!hasSignature}
          whileHover={hasSignature ? { scale:1.02 } : {}} whileTap={hasSignature ? { scale:0.97 } : {}}
          style={{ flex:2, padding:'8px', borderRadius:'10px', border:'none', background: hasSignature ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'rgba(255,255,255,0.06)', color: hasSignature ? 'white' : 'rgba(255,255,255,0.25)', fontSize:'12px', fontWeight:700, cursor: hasSignature ? 'pointer' : 'not-allowed', fontFamily:'inherit' }}>
          {hasSignature ? t('confirm_signature',{ns:'admin'}) : t('sign_here',{ns:'admin'})}
        </motion.button>
      </div>
    </div>
  );
};

const FlowResults = ({ data, aircraft, darkMode, onClose, currentUser }) => {
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [showSignature, setShowSignature] = useState(false);

  if (!data) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.button onClick={onClose} whileTap={{ scale: 0.95 }}
          style={{ padding: '14px 32px', background: 'linear-gradient(135deg,#2563eb,#6d28d9)', color: 'white', border: 'none', borderRadius: '14px', fontWeight: 700, fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' }}>
          Volver al inicio
        </motion.button>
      </div>
    );
  }

  const {
    total_items        = 0,
    trap_confirmed     = [],
    trap_rejected      = [],
    trap_ids           = [],
    avg_response_ms    = 0,
    total_time_ms      = 0,
    total_inspections  = 0,
    flow_mode_unlocked = false,
    events             = [],
    anomalies          = [],
    hobbs_value        = null,
  } = data;

  const hasTrapIssues           = trap_confirmed.length > 0;
  const totalTraps              = trap_ids.length;
  const trapsCorrectlyRejected  = trap_rejected.length;

  // Items reales del checklist (excluir trampas)
  const trapIdSet    = new Set(trap_ids);
  const checkedItems = events.filter(e => e.event_type === 'trap_confirmed' && !trapIdSet.has(e.item_id));
  const naItems      = events.filter(e => e.event_type === 'trap_rejected'  && !trapIdSet.has(e.item_id));

  // Agrupar por sección para el PDF
  const itemsBySection = {};
  [...checkedItems, ...naItems].forEach(e => {
    const section = e.item_section || 'General';
    if (!itemsBySection[section]) itemsBySection[section] = [];
    itemsBySection[section].push(e);
  });
  const hasItems    = checkedItems.length + naItems.length > 0;
  const hasAnomalies = anomalies.length > 0;

  // ── PDF ──────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    setPdfLoading(true);
    try {
      const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW  = doc.internal.pageSize.getWidth();
      const pageH  = doc.internal.pageSize.getHeight();
      const margin = 18;
      const cW     = pageW - margin * 2;
      let y        = 0;

      const pilotName    = currentUser?.name || 'Piloto';
      const aircraftName = aircraft.registration || aircraft.name || 'Aeronave';
      const aircraftFull = aircraft.manufacturer
        ? `${aircraft.manufacturer} ${aircraft.model_name || ''}`.trim()
        : aircraftName;
      const now     = new Date();
      const dateStr = now.toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
      const timeStr = now.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });

      const lc = [100, 116, 139];
      const vc = [15, 23, 42];

      const drawHeader = () => {
        doc.setFillColor(37, 99, 235);
        doc.rect(0, 0, pageW, 14, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255);
        doc.text('CONDOR360 — GESTION AERONAUTICA', margin, 9.5);
        doc.text(`${dateStr}  ${timeStr}`, pageW - margin, 9.5, { align: 'right' });
        y = 22;
      };

      const drawFooter = () => {
        const total = doc.internal.getNumberOfPages();
        for (let i = 1; i <= total; i++) {
          doc.setPage(i);
          doc.setFillColor(245, 247, 250);
          doc.rect(0, pageH - 13, pageW, 13, 'F');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6.5);
          doc.setTextColor(120, 120, 120);
          doc.text('Checklist generado por Condor360 - herramienta de asistencia administrativa. No reemplaza los procedimientos oficiales del fabricante (POH).', margin, pageH - 5.5);
          doc.text('La responsabilidad final del vuelo es del Piloto al Mando.', margin, pageH - 2.5);
          doc.text(`${i} / ${total}`, pageW - margin, pageH - 3.5, { align: 'right' });
        }
        doc.setTextColor(0, 0, 0);
      };

      const checkY = (n = 10) => {
        if (y + n > pageH - margin - 14) {
          doc.addPage();
          drawHeader();
        }
      };

      drawHeader();

      // ── Banner estado ──────────────────────────────────────────
      if (hasTrapIssues) {
        doc.setFillColor(254, 243, 199);
        doc.roundedRect(margin, y, cW, 16, 3, 3, 'F');
        doc.setDrawColor(251, 191, 36);
        doc.setLineWidth(0.5);
        doc.roundedRect(margin, y, cW, 16, 3, 3, 'S');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(146, 64, 14);
        doc.text('FLOW COMPLETADO CON OBSERVACIONES', pageW / 2, y + 10.5, { align: 'center' });
      } else {
        doc.setFillColor(220, 252, 231);
        doc.roundedRect(margin, y, cW, 16, 3, 3, 'F');
        doc.setDrawColor(134, 239, 172);
        doc.setLineWidth(0.5);
        doc.roundedRect(margin, y, cW, 16, 3, 3, 'S');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(21, 128, 61);
        doc.text('FLOW MODE COMPLETADO', pageW / 2, y + 10.5, { align: 'center' });
      }
      y += 22;

      // ── Datos del vuelo ────────────────────────────────────────
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin, y, cW, 36, 3, 3, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(margin, y, cW, 36, 3, 3, 'S');

      const col1 = margin + 5, col2 = pageW / 2 + 2;
      const infoRow = (lbl, val, x, row) => {
        const ry = y + 8 + row * 11;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...lc);
        doc.text(lbl.toUpperCase(), x, ry);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...vc);
        doc.text(sanitizePDF(val) || '-', x, ry + 4.5);
      };
      infoRow('Aeronave',   aircraftFull,                  col1, 0);
      infoRow('Matricula',  aircraft.registration || 'N/D', col2, 0);
      infoRow('Piloto',     pilotName,                     col1, 1);
      infoRow('Fecha/Hora', `${dateStr}  ${timeStr}`,      col2, 1);
      y += 42;

      // ── Banner AOG en PDF ────────────────────────────────────────
      const aogAnomalies = anomalies.filter(a => a.is_aog);
      if (aogAnomalies.length > 0) {
        doc.setFillColor(239, 68, 68);
        doc.roundedRect(margin, y, cW, 10, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
        doc.text('AVION BLOQUEADO - AOG ACTIVO', margin + 6, y + 7);
        y += 13;
        doc.setFillColor(254, 226, 226);
        doc.roundedRect(margin, y, cW, 6 + aogAnomalies.length * 7, 2, 2, 'F');
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(153, 27, 27);
        aogAnomalies.forEach((a, i) => {
          doc.text(`• ${sanitizePDF(a.text)}`, margin + 4, y + 5 + i * 7);
        });
        y += 8 + aogAnomalies.length * 7;
        doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(156, 163, 175);
        doc.text('El avion queda fuera de servicio hasta que un mecanico resuelva el squawk.', margin, y);
        y += 10;
      }

      // ── Stats cards ────────────────────────────────────────────
      const stats = [
        { label: 'Items completados',  value: String(total_items),  color: [129, 140, 248] },
        { label: 'Tiempo total',       value: formatTime(total_time_ms), color: [6, 182, 212] },
        { label: 'Promedio por item',  value: avg_response_ms ? `${(avg_response_ms / 1000).toFixed(1)}s` : 'N/A', color: [139, 92, 246] },
      ];
      const stW = cW / 3 - 2;
      stats.forEach((s, i) => {
        const sx = margin + i * (stW + 3);
        doc.setFillColor(...s.color);
        doc.roundedRect(sx, y, stW, 22, 3, 3, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
        doc.text(s.value, sx + stW / 2, y + 13, { align: 'center' });
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
        doc.text(s.label, sx + stW / 2, y + 19, { align: 'center' });
      });
      y += 30;

      // ── Total inspecciones ─────────────────────────────────────
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...lc);
      doc.text(`Total inspecciones completadas con Flow Mode: ${total_inspections}`, margin, y);
      y += 10;

      // ── Sección trampas ────────────────────────────────────────
      if (totalTraps > 0) {
        checkY(20);
        doc.setFillColor(245, 158, 11);
        doc.rect(margin, y - 5, cW, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
        doc.text(`ITEMS TRAMPA (${totalTraps})`, margin + 4, y);
        y += 10;

        if (trapsCorrectlyRejected > 0) {
          checkY(8 + trap_rejected.length * 6);
          doc.setFillColor(220, 252, 231);
          doc.roundedRect(margin, y - 4, cW, 8 + trap_rejected.length * 6, 2, 2, 'F');
          doc.setTextColor(22, 101, 52);
          doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
          doc.text(`Rechazadas correctamente: ${trapsCorrectlyRejected}`, margin + 4, y);
          y += 7;
          doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
          trap_rejected.forEach(t => { doc.text(`  + ${sanitizePDF(t.label)}`, margin + 6, y); y += 5; });
          y += 5;
        }

        if (hasTrapIssues) {
          checkY(16 + trap_confirmed.length * 6);
          doc.setFillColor(254, 243, 199);
          doc.roundedRect(margin, y - 4, cW, 16 + trap_confirmed.length * 6, 2, 2, 'F');
          doc.setTextColor(146, 64, 14);
          doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
          doc.text(`Confirmadas por error: ${trap_confirmed.length}`, margin + 4, y);
          y += 7;
          doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
          trap_confirmed.forEach(t => { doc.text(`  - ${sanitizePDF(t.label)}`, margin + 6, y); y += 5; });
          y += 4;
          doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(120, 60, 0);
          const warn = 'Revisar el procedimiento con el instructor para reforzar la identificacion de cada punto del checklist.';
          const warnLines = doc.splitTextToSize(warn, cW - 8);
          warnLines.forEach(line => { doc.text(line, margin + 4, y); y += 5; });
          y += 4;
        }
      } else {
        // Sin trampas — mostrar resultado perfecto
        checkY(14);
        doc.setFillColor(220, 252, 231);
        doc.roundedRect(margin, y - 4, cW, 12, 2, 2, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(21, 128, 61);
        doc.text('Sin items trampa en este flujo', margin + 4, y + 3);
        y += 14;
      }

      // ── Detalle de items del checklist ────────────────────────────
      if (checkedItems.length + naItems.length > 0) {
        checkY(14);
        doc.setFillColor(37, 99, 235);
        doc.rect(margin, y - 5, cW, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
        doc.text('DETALLE DEL CHEQUEO', margin + 4, y);
        y += 10;
        const allCheckItems = [...checkedItems, ...naItems];
        allCheckItems.forEach((e, idx) => {
          checkY(7);
          const isOk = e.event_type === 'trap_confirmed';
          if (idx % 2 === 0) { doc.setFillColor(249, 250, 251); doc.rect(margin, y - 1, cW, 7, 'F'); }
          if (isOk) {
            doc.setFillColor(22, 163, 74); doc.circle(margin + 4, y + 2.5, 2.5, 'F');
          } else {
            doc.setFillColor(234, 88, 12); doc.circle(margin + 4, y + 2.5, 2.5, 'F');
            doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(255, 255, 255);
            doc.text('x', margin + 4, y + 3.8, { align: 'center' });
          }
          doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
          doc.setTextColor(isOk ? 30 : 156, isOk ? 30 : 163, isOk ? 30 : 175);
          doc.text(sanitizePDF(e.item_label || ''), margin + 10, y + 3.5);
          const tag = isOk ? 'OK' : 'N/A';
          const tc  = isOk ? [220, 252, 231] : [255, 237, 213];
          const tt  = isOk ? [21, 128, 61]   : [154, 52, 18];
          doc.setFillColor(...tc); doc.roundedRect(pageW - margin - 14, y, 14, 5.5, 1, 1, 'F');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(...tt);
          doc.text(tag, pageW - margin - 7, y + 3.8, { align: 'center' });
          y += 7;
        });
        y += 4;
      }

      // ── Horómetro ────────────────────────────────────────────────
      if (hobbs_value) {
        checkY(12);
        doc.setFillColor(241, 245, 249);
        doc.rect(margin, y - 1, cW, 9, 'F');
        doc.setFillColor(99, 102, 241); doc.circle(margin + 4, y + 3.5, 2.5, 'F');
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(30, 30, 30);
        doc.text('Registro de horometro', margin + 10, y + 4.5);
        const hobbsTc = [220, 252, 231]; const hobbsTt = [21, 128, 61];
        doc.setFillColor(...hobbsTc); doc.roundedRect(pageW - margin - 28, y, 28, 5.5, 1, 1, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(...hobbsTt);
        doc.text(`${hobbs_value} hs`, pageW - margin - 14, y + 3.8, { align: 'center' });
        y += 9;
      }

      // ── Observaciones registradas durante el flow ──────────────
      if (anomalies.length > 0) {
        checkY(20);
        doc.setFillColor(254, 243, 199);
        doc.roundedRect(margin, y - 5, cW, 8, 1.5, 1.5, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(146, 64, 14);
        doc.text(`OBSERVACIONES EN VUELO (${anomalies.length})`, margin + 4, y);
        y += 10;
        anomalies.forEach((obs, idx) => {
          checkY(12);
          const isAog    = obs.is_aog;
          const isSquawk = obs.type === 'squawk';
          const bgColor  = isAog ? [254, 226, 226] : isSquawk ? [255, 237, 213] : [255, 251, 235];
          const txtColor = isAog ? [153, 27, 27]   : isSquawk ? [154, 52, 18]   : [146, 64, 14];
          const tag      = isAog ? 'AOG' : isSquawk ? 'SQUAWK' : 'OBS';
          const tagBg    = isAog ? [254, 202, 202] : isSquawk ? [254, 215, 170] : [254, 240, 138];
          const tagTxt   = isAog ? [185, 28, 28]   : isSquawk ? [154, 52, 18]   : [133, 77, 14];
          doc.setFillColor(...bgColor); doc.rect(margin, y - 1, cW, 9, 'F');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...txtColor);
          doc.text(`${idx + 1}.  ${sanitizePDF(obs.text)}`, margin + 4, y + 4.5);
          doc.setFillColor(...tagBg); doc.roundedRect(pageW - margin - 18, y + 1, 18, 5.5, 1, 1, 'F');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(...tagTxt);
          doc.text(tag, pageW - margin - 9, y + 4.8, { align: 'center' });
          y += 10;
        });
        y += 4;
      }

      // ── Separador + firma ──────────────────────────────────────
      checkY(30);
      y += 4;
      doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.4);
      doc.line(margin, y, pageW - margin, y);
      y += 8;
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...vc);
      doc.text('Observaciones adicionales:', margin, y); y += 5;
      doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.3);
      for (let i = 0; i < 3; i++) doc.line(margin, y + i * 7, pageW - margin, y + i * 7);
      y += 28;
      doc.line(margin, y, margin + 60, y);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...lc);
      doc.text('Firma del piloto', margin, y + 4);

      // Firma digital si existe
      if (signatureData) {
        try {
          doc.addImage(signatureData, 'PNG', margin, y - 28, 60, 26);
        } catch (_) {}
      }

      drawFooter();

      const dateFile = now.toISOString().slice(0, 10).replace(/-/g, '');
      const acFile   = `${aircraft.manufacturer || 'Aeronave'}_${aircraft.model_name || ''}`.replace(/\s+/g, '_');
      doc.save(`Condor360_FlowMode_${acFile}_${dateFile}.pdf`);
    } catch (err) {
      console.error('[FlowResults] Error generando PDF:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#0f172a', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div style={{ maxWidth: '480px', margin: '0 auto', padding: '40px 24px 60px' }}>

        {/* ── Header ─────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 120, damping: 18 }}
          style={{ textAlign: 'center', marginBottom: '32px' }}
        >
          <div style={{
            width: '72px', height: '72px', borderRadius: '50%', margin: '0 auto 16px',
            background:  hasTrapIssues ? 'rgba(245,158,11,0.1)' : 'rgba(34,197,94,0.1)',
            border: `2px solid ${hasTrapIssues ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.3)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {hasTrapIssues
              ? <AlertTriangle style={{ width: '32px', height: '32px', color: '#f59e0b' }} />
              : <CheckCircle  style={{ width: '32px', height: '32px', color: '#22c55e' }} />
            }
          </div>
          <h1 style={{ margin: '0 0 6px', fontSize: '24px', fontWeight: 800, color: '#f1f5f9' }}>
            {hasTrapIssues ? 'Flow completado con observaciones' : '¡Flow completado!'}
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
            {aircraft.registration || aircraft.name}
          </p>
        </motion.div>

        {/* ── Banner AOG ─────────────────────────────────────────────── */}
        {anomalies.some(a => a.is_aog) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, type: 'spring', stiffness: 140, damping: 18 }}
            style={{ marginBottom: '24px', borderRadius: '18px', overflow: 'hidden',
              border: '1.5px solid rgba(239,68,68,0.4)',
              boxShadow: '0 4px 24px rgba(239,68,68,0.2)' }}
          >
            <div style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)', padding: '14px 18px',
              display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>⛔</span>
              <div>
                <p style={{ margin: 0, fontSize: '15px', fontWeight: 800, color: 'white' }}>
                  AVIÓN BLOQUEADO — AOG ACTIVO
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>
                  Reportaste un squawk AOG durante este chequeo
                </p>
              </div>
            </div>
            <div style={{ background: 'rgba(239,68,68,0.06)', padding: '12px 18px' }}>
              {anomalies.filter(a => a.is_aog).map((a, i) => (
                <p key={i} style={{ margin: i > 0 ? '6px 0 0' : 0, fontSize: '13px',
                  color: '#fca5a5', fontWeight: 600 }}>
                  • {a.text}
                </p>
              ))}
              <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#94a3b8', lineHeight: 1.5 }}>
                El avión queda fuera de servicio hasta que un mecánico resuelva el squawk.
                El instructor fue notificado.
              </p>
            </div>
          </motion.div>
        )}

        {/* ── Stats ──────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
          <StatCard icon={Zap}   label="Items"   value={total_items}                   color="#818cf8" delay={0.1} />
          <StatCard icon={Clock} label="Tiempo"  value={formatTime(total_time_ms)}     color="#06b6d4" delay={0.2} />
          <StatCard icon={Zap}   label="Promedio" value={avg_response_ms ? `${(avg_response_ms / 1000).toFixed(1)}s` : '—'} color="#8b5cf6" delay={0.3} />
        </div>

        {/* ── Trampas ────────────────────────────────────────────── */}
        {totalTraps > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 700, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertTriangle style={{ width: '16px', height: '16px', color: '#f59e0b' }} />
              Items trampa ({totalTraps})
            </h3>
            {trapsCorrectlyRejected > 0 && (
              <div style={{ padding: '14px 16px', borderRadius: '14px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)', marginBottom: '10px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#4ade80', fontWeight: 600 }}>
                  ✓ {trapsCorrectlyRejected} {trapsCorrectlyRejected === 1 ? 'trampa rechazada' : 'trampas rechazadas'} correctamente
                </p>
                {trap_rejected.map((t, i) => (
                  <p key={i} style={{ margin: '6px 0 0', fontSize: '12px', color: '#94a3b8' }}>• {t.label}</p>
                ))}
              </div>
            )}
            {hasTrapIssues && (
              <div style={{ padding: '14px 16px', borderRadius: '14px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#fbbf24', fontWeight: 600 }}>
                  ⚠ Confirmaste {trap_confirmed.length} {trap_confirmed.length === 1 ? 'item que no corresponde' : 'items que no corresponden'} a esta aeronave
                </p>
                {trap_confirmed.map((t, i) => (
                  <p key={i} style={{ margin: '6px 0 0', fontSize: '12px', color: '#94a3b8' }}>• {t.label}</p>
                ))}
                <p style={{ margin: '12px 0 0', fontSize: '12px', color: '#94a3b8', lineHeight: 1.6 }}>
                  {trap_confirmed.length === 1
                    ? 'Este item no forma parte del checklist de esta aeronave. Revisá el procedimiento con tu instructor.'
                    : 'Estos items no forman parte del checklist de esta aeronave. Revisá el procedimiento con tu instructor.'}
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* ── Flow mode unlocked ──────────────────────────────────── */}
        {flow_mode_unlocked && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, type: 'spring' }}
            style={{ padding: '20px', borderRadius: '18px', background: 'linear-gradient(135deg, rgba(109,40,217,0.12), rgba(37,99,235,0.08))', border: '1.5px solid rgba(109,40,217,0.25)', marginBottom: '24px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '28px' }}>🌀</p>
            <p style={{ margin: '8px 0 4px', fontSize: '16px', fontWeight: 800, color: '#c4b5fd' }}>¡Flow Mode desbloqueado!</p>
            <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8', lineHeight: 1.5 }}>
              Completaste {total_inspections} inspecciones. A partir de ahora podés usar el Flow Mode en todas tus inspecciones.
            </p>
          </motion.div>
        )}

        {/* ── Total inspecciones ──────────────────────────────────── */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          style={{ textAlign: 'center', marginBottom: '24px' }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#475569' }}>
            Total inspecciones completadas: <strong style={{ color: '#94a3b8' }}>{total_inspections}</strong>
          </p>
        </motion.div>

        {/* ── Firma digital ──────────────────────────────────────── */}
        <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.65 }}
          style={{ marginBottom:'16px' }}>
          {!signatureData ? (
            <div style={{ borderRadius:'16px', padding:'14px', background:'rgba(37,99,235,0.07)', border:'1px solid rgba(37,99,235,0.15)' }}>
              <p style={{ margin:'0 0 10px', fontSize:'12px', fontWeight:700, color:'#93c5fd', textAlign:'center' }}>
                Firma digital del piloto
              </p>
              <SignaturePad
                onSave={(data) => { setSignatureData(data); setShowSignature(false); }}
                onClear={() => setSignatureData(null)}
              />
            </div>
          ) : (
            <div style={{ borderRadius:'14px', padding:'10px 14px', background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.2)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <span style={{ fontSize:'16px' }}>✅</span>
                <span style={{ fontSize:'13px', fontWeight:700, color:'#4ade80' }}>Firma registrada</span>
              </div>
              <button onClick={() => setSignatureData(null)}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:'11px', color:'rgba(255,255,255,0.4)', fontFamily:'inherit' }}>
                Cambiar
              </button>
            </div>
          )}
        </motion.div>

        {/* ── Botones ─────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <motion.button
            onClick={handleExportPDF}
            disabled={pdfLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            style={{
              width: '100%', padding: '14px', borderRadius: '18px',
              border: '1.5px solid rgba(129,140,248,0.25)', background: 'rgba(129,140,248,0.08)',
              color: '#a5b4fc', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              fontFamily: 'inherit', opacity: pdfLoading ? 0.7 : 1,
            }}
          >
            {pdfLoading
              ? <><Loader style={{ width: '15px', height: '15px', animation: 'spin 1s linear infinite' }} /> Generando PDF...</>
              : <><FileDown style={{ width: '15px', height: '15px' }} /> {signatureData ? 'Exportar PDF con firma' : 'Exportar resumen'}</>
            }
          </motion.button>

          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            style={{
              width: '100%', padding: '18px', borderRadius: '18px', border: 'none',
              background: 'linear-gradient(135deg, #2563eb, #6d28d9)', color: 'white',
              fontSize: '16px', fontWeight: 800, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              boxShadow: '0 4px 20px rgba(37,99,235,0.3)', fontFamily: 'inherit',
            }}
          >
            Finalizar <ArrowRight style={{ width: '18px', height: '18px' }} />
          </motion.button>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default FlowResults;
